import first from 'lodash/first';
import uniq from 'lodash/uniq';
import AirPnrManager from '../../services/AirPnrManager';
import V2TripDetailsResponseManager from '../../services/V2TripDetailsResponseManager';
import { MoneyUtil } from '../Money';
import type { IFareComponent, IPaxFareBreakup } from '../../types/flight';
import type { ITripPnr } from '../../types/trip';
import { AirPnrTravelerInfoSeatInfoSeatStatus } from '../../types/trip';
import { ungroupPaxInfos } from './index';
import type { IUseGetItineraryFareBreakup } from '..';
import { getTripsTotalServiceFees } from '..';
import { getMerchantFeeFromPnr } from '../trips';
import { OtherAncillaryAncillaryStatus } from '../../types/api/v1/obt/pnr/pnr_common';
import { OtherAncillaryTypeToNameMap } from '../../constants/trips';

interface IProps {
  pnr: ITripPnr;
  showServiceFeeTax: boolean;
}

const getTripFareBreakup = ({ pnr, showServiceFeeTax }: IProps): IUseGetItineraryFareBreakup => {
  // eslint-disable-next-line no-restricted-syntax
  const data = V2TripDetailsResponseManager.GetAirPnrCardDetails(pnr);

  const { totalFare } = data;

  const currency = totalFare.getCurrency();
  const originalCurrency = totalFare.getOriginalCurrency();
  const zeroMoney = MoneyUtil.zeroMoneyWithOriginal(currency, originalCurrency);

  const groupedPaxInfo = pnr.air?.air?.itineraries.flatMap((itin) => itin.rateOptions.flatMap((rate) => rate.paxInfo));
  const merchantFee = getMerchantFeeFromPnr(pnr);

  const paxInfos = ungroupPaxInfos(groupedPaxInfo ?? []);
  const travelerInfos = pnr.air?.travelerInfo.map((traveler) => traveler);
  const seatInfos = travelerInfos?.map((traveler) => traveler.seatInfo);
  const ticketType = AirPnrManager.GetTicketType(pnr);

  const seatPricePerPax = seatInfos?.map((seats) => {
    if (!seats.length) {
      return zeroMoney;
    }
    let pricePerPax = zeroMoney;
    seats.forEach(({ amount, status }) => {
      const isSeatNonDeclined =
        status === AirPnrTravelerInfoSeatInfoSeatStatus.CONFIRMED ||
        status === AirPnrTravelerInfoSeatInfoSeatStatus.PENDING;

      const seatPrice = isSeatNonDeclined && amount ? MoneyUtil.parse(amount) : zeroMoney;

      pricePerPax = pricePerPax.add(seatPrice);
    });
    return pricePerPax;
  });
  let paxesFareBreakup: IPaxFareBreakup[] = [];
  let totalSeatPrice = zeroMoney;
  const totalBaggagePrice = zeroMoney;

  paxInfos?.forEach((_paxInfo, paxIndex) => {
    const seatPrice = seatPricePerPax ? seatPricePerPax[paxIndex] : zeroMoney;
    totalSeatPrice = totalSeatPrice.add(seatPrice);
  });

  const paxes = paxInfos?.map((pax, paxIndex) => {
    // TODO: move to some manager method that should return MoneyUtil Instance instead of creating it here
    const basePerPax = pax.farePerPax?.base;
    const taxPerPax = pax.farePerPax?.tax;

    const baseMoneyUtil = MoneyUtil.parse(basePerPax);
    const taxMoneyUtil = MoneyUtil.parse(taxPerPax);

    const seatPrice = seatPricePerPax ? seatPricePerPax[paxIndex] : zeroMoney;

    let flightCreditPricePerPax = zeroMoney;
    let eligibleRefund = zeroMoney;
    let paxTotal = zeroMoney;
    let penaltyFees = zeroMoney;
    let paxBasePlusTaxFare = zeroMoney;

    try {
      // Base+Tax calculation
      paxBasePlusTaxFare = baseMoneyUtil.add(taxMoneyUtil);
      paxTotal = paxTotal.add(paxBasePlusTaxFare);
    } catch {
      // noop
    }
    try {
      // FLIGHT CREDIT PRICE CALCULATION
      // currently only 1 applied credit is available to apply
      const flightCredit = first(travelerInfos?.[paxIndex]?.appliedCredits);
      const flightCreditBaseFare = flightCredit ? MoneyUtil.parse(flightCredit?.creditAmount?.base) : zeroMoney;
      const flightCreditTaxFare = flightCredit ? MoneyUtil.parse(flightCredit?.creditAmount?.tax) : zeroMoney;

      const eligibleRefundFare = flightCredit ? MoneyUtil.parse(flightCredit?.fareDifference) : zeroMoney;
      const flightCreditBasePlusTax = flightCreditBaseFare.add(flightCreditTaxFare);

      penaltyFees = flightCredit?.penalty ? MoneyUtil.parse(flightCredit.penalty) : zeroMoney;

      // flight credit should be applied only for the primary traveler in case of multipax
      flightCreditPricePerPax = paxIndex === 0 ? flightCreditBasePlusTax : zeroMoney;

      // refund is issued only if the value is negative, otherwise we should not show this
      eligibleRefund = eligibleRefundFare.isNegative()
        ? MoneyUtil.parse(flightCredit?.fareDifference).multiply(-1)
        : zeroMoney;

      const flightCreditsLessOrEqualToBaseTax = flightCreditPricePerPax.compare(paxBasePlusTaxFare) <= 0;
      if (flightCreditsLessOrEqualToBaseTax) {
        // Remove base+tax from paxTotal
        paxTotal = paxTotal.subtract(paxBasePlusTaxFare);
        // Add difference base+tax and credits to paxTotal
        paxTotal = paxTotal.add(paxBasePlusTaxFare.subtract(flightCreditPricePerPax));
      } else {
        // Only remove base+tax from paxTotal as flight credits is more than
        // base+tax. Max refund from credits is equal to base+tax.
        paxTotal = paxTotal.subtract(paxBasePlusTaxFare);
      }
    } catch {
      // noop
    }

    try {
      // Add seat price
      paxTotal = paxTotal.add(seatPrice);
    } catch {
      // noop
    }

    const legBaseFare = AirPnrManager.GetTicketPricesAndAirportsForMultiTicket(pnr, paxIndex);

    const baggage = AirPnrManager.GetExtraBaggageFee(pnr, paxIndex);
    try {
      // Add baggage price
      paxTotal = paxTotal.add(baggage);
    } catch {
      // noop
    }

    let otherAncillariesTotalAmount = zeroMoney;
    const ancillaryNames: string[] = [];

    try {
      const otherAncillaries = travelerInfos?.[paxIndex].otherAncillaries || [];
      const confirmedOtherAncillaries = otherAncillaries.filter(
        (ancillaryInfo) => ancillaryInfo.status === OtherAncillaryAncillaryStatus.CONFIRMED,
      );
      confirmedOtherAncillaries.forEach((ancillaryInfo) => {
        const ancillaryFareBase = ancillaryInfo.fare?.base ? MoneyUtil.parse(ancillaryInfo.fare.base) : zeroMoney;
        const ancillaryFareTax = ancillaryInfo.fare?.tax ? MoneyUtil.parse(ancillaryInfo.fare.tax) : zeroMoney;

        otherAncillariesTotalAmount = otherAncillariesTotalAmount.add(ancillaryFareBase).add(ancillaryFareTax);

        ancillaryNames.push(OtherAncillaryTypeToNameMap[ancillaryInfo.type]);
      });

      paxTotal = paxTotal.add(otherAncillariesTotalAmount);
    } catch {
      // noop
    }

    const fareComponents: IFareComponent = {
      baseFare: baseMoneyUtil,
      feesAndTaxes: taxMoneyUtil,
      penaltyFees,
      flightCreditPricePerPax,
      eligibleRefund,
      seat: seatPrice,
      baggage,
      // UAPassPlus fare information is not forwarded on trips page. It's already included as part of totalFare.
      uaPassPlusBaseFare: zeroMoney,
      uaPassPlusFeesAndTax: zeroMoney,
      legBaseFare,
      appliedCreditAirlineCode: '',
      zeroMoney,
      otherAncillary: {
        totalPrice: otherAncillariesTotalAmount,
        ancillaries: uniq(ancillaryNames),
      },
    };

    return {
      paxType: pax.paxType,
      paxTotal,
      fareComponents,
      // Used to be compatible and render traveler names with the /Flights/useItineraryBreakup hook
      paxUserId: travelerInfos?.[paxIndex]?.traveler?.userOrgId?.userId?.id,
    };
  });

  if (paxes && paxes.length > 0) {
    paxesFareBreakup = paxes;
  }

  const totalAmountWithoutPaymentFees = paxesFareBreakup.reduce(
    (total, paxInfo) => paxInfo.paxTotal.add(total),
    zeroMoney,
  );

  const totalAmount = MoneyUtil.parse(pnr?.totalFare); // BE will add seat, baggage and payment fees here itself

  const totalTicketPrice = totalAmountWithoutPaymentFees;

  const paymentFees = zeroMoney;

  const isSameCurrency = currency === originalCurrency;

  const allowedFop = pnr.air?.air?.itineraries[0].rateOptions[0].allowedFop ?? [];

  const primaryTravelerFare = paxes[0].fareComponents;

  const primaryTravelerTicketPrice = primaryTravelerFare.baseFare.add(primaryTravelerFare.feesAndTaxes);

  const { serviceFees } = getTripsTotalServiceFees({
    serviceCharges: pnr.serviceFees,
    showServiceFeeTax: showServiceFeeTax ?? false,
  });

  const totalAmountExcludingServiceFees = totalAmount.subtract(serviceFees.total);
  /**
   * Workaround to display unused credits in Payment Details from Trips page, using paxesFareBreakup[0] because currently
   * credits is only allowed for single pax
   */
  const unusedCredits = paxes[0].fareComponents.flightCreditPricePerPax;

  return {
    isSameCurrency,
    totalAmount,
    paxesFareBreakup,
    paymentFees,
    totalTicketPrice,
    allowedFop,
    primaryTravelerTicketPrice,
    ticketType,
    serviceFees,
    merchantFee,
    totalAmountExcludingServiceFees,
    showBrexPointsFare: true,
    totalBaggagePrice,
    totalSeatPrice,
    unusedCredits,
  };
};

export default getTripFareBreakup;
